import React, { FormEvent, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';

import type { RootState } from '@/rootReducer';
import { CloseThin } from '@/resources/assets/images';
import {
	userClearOtpState,
	userClearOtpSuccessFailureStates,
	userDeactivateOtp,
	userEnableOtp,
	userGenerateOtpSecret
} from '../../userActions';
import './TwoFactorAuthModal.scss';
import Loader, { Status } from '@/components/Loader/Loader';

interface TwoFactorAuthModalProps {
	setShow2faModal: (x: boolean) => void;
}
const TwoFactorAuthModal = ({
	setShow2faModal
}: TwoFactorAuthModalProps): JSX.Element => {
	const dispatch = useDispatch();
	const [
		user,
		otpAuthUrl,
		otpGenerateOtpSecretLoading,
		otpGenerateOtpSecretFailure,
		otpRecoveryCodes,
		otpEnableLoading,
		otpEnableFailure,
		otpDeactivateLoading,
		otpDeactivateSuccess,
		otpDeactivateFailure
	] = useSelector(
		(state: RootState) => [
			state.user.getIn(['authorize', 'user']).toJS(),
			state.user.getIn(['otp', 'generateOtpSecret', 'authUrl']),
			state.user.getIn(['otp', 'generateOtpSecret', 'loading']),
			state.user.getIn(['otp', 'generateOtpSecret', 'failure']),
			state.user.getIn(['otp', 'enable', 'recoveryCodes']),
			state.user.getIn(['otp', 'enable', 'loading']),
			state.user.getIn(['otp', 'enable', 'failure']),
			state.user.getIn(['otp', 'deactivate', 'loading']),
			state.user.getIn(['otp', 'deactivate', 'success']),
			state.user.getIn(['otp', 'deactivate', 'failure'])
		],
		shallowEqual
	);
	const [code2faInputValue, setCode2faInputValue] = useState<string>('');

	const generateOtpSecret = () => dispatch(userGenerateOtpSecret());
	const enableOtp = (otp: string) => dispatch(userEnableOtp({ otp }));
	const deactivateOtp = () => dispatch(userDeactivateOtp());
	const clearOtpSuccessFailureStates = () =>
		dispatch(userClearOtpSuccessFailureStates());
	const clearOtpState = () => dispatch(userClearOtpState());

	const handleEnable2fa = () => {
		generateOtpSecret();
		clearOtpSuccessFailureStates();
	};

	const handleDeactivate2fa = () => {
		deactivateOtp();
	};

	const handle2faCodeInput = (e: FormEvent<HTMLInputElement>) => {
		setCode2faInputValue((e.target as HTMLInputElement).value);
	};

	const handle2faCodeSubmit = (
		e: FormEvent<HTMLButtonElement> | FormEvent<HTMLFormElement>
	) => {
		e.preventDefault();
		if (code2faInputValue.length === 6) {
			enableOtp(code2faInputValue);
			setCode2faInputValue('');
		}
	};

	const onClose = () => {
		setShow2faModal(false);
		clearOtpState();
		clearOtpSuccessFailureStates();
	};

	useEffect(() => {
		if (otpDeactivateSuccess) {
			clearOtpState();
		}
	}, [otpDeactivateSuccess]);

	return (
		<div className="two-factor-auth-modal">
			<div className="two-factor-auth-modal-bg" />
			<div className="two-factor-auth-modal-content">
				<CloseThin
					onClick={onClose}
					className="two-factor-auth-modal-close-icon"
				/>
				<div className="two-factor-auth-modal-header">
					Two-Factor Authentication
				</div>
				{otpDeactivateSuccess && (
					<span className="two-factor-auth-modal-deactivate-success-text">
						2FA has been deactivated successfully!
					</span>
				)}
				{!user?.otpVerified && (
					<div className="two-factor-auth-modal-activate">
						{otpGenerateOtpSecretFailure && (
							<span className="two-factor-auth-modal-activate-failure-text">
								Failed to fetch the QR code. Please try again.
							</span>
						)}
						<div
							className="two-factor-auth-modal-activate-button"
							onClick={handleEnable2fa}
						>
							{!otpGenerateOtpSecretLoading && 'Enable 2FA'}
							{otpGenerateOtpSecretLoading && (
								<Loader
									className="two-factor-auth-modal-activate-button-loading"
									status={Status.LOADING}
								/>
							)}
						</div>
					</div>
				)}
				{user?.otpVerified && (
					<div className="two-factor-auth-modal-deactivate">
						{otpDeactivateFailure && (
							<span className="two-factor-auth-modal-deactivate-failure-text">
								Failed to deactivate 2FA. Please try again.
							</span>
						)}
						<div
							className="two-factor-auth-modal-deactivate-button"
							onClick={handleDeactivate2fa}
						>
							{!otpDeactivateLoading && 'Deactivate 2FA'}
							{otpDeactivateLoading && (
								<Loader
									className="two-factor-auth-modal-deactivate-button-loading"
									status={Status.LOADING}
								/>
							)}
						</div>
					</div>
				)}
				{otpAuthUrl && (
					<div className="two-factor-auth-modal-qr-code">
						<span className="two-factor-auth-modal-qr-code-text">
							Please scan the QR code below with your
							authenticator app
						</span>
						<QRCodeSVG value={otpAuthUrl} size={200} />
					</div>
				)}
				{!user?.otpVerified && (
					<>
						<header>
							Enter the code generated by your authenticator app
						</header>
						<form
							className="two-factor-auth-modal-2fa-code"
							onSubmit={handle2faCodeSubmit}
						>
							{otpEnableFailure && (
								<span className="two-factor-auth-modal-2fa-code-failure-text">
									This code is incorrect. Please try again.
								</span>
							)}
							<input
								className="two-factor-auth-modal-2fa-code-input"
								type="text"
								value={code2faInputValue}
								onChange={handle2faCodeInput}
							/>
							<div
								className={`two-factor-auth-modal-2fa-code-submit-button ${code2faInputValue.length !== 6 ? 'disabled' : ''} `}
								onClick={handle2faCodeSubmit}
							>
								{!otpEnableLoading && 'Submit'}
								{otpEnableLoading && (
									<Loader
										className="two-factor-auth-modal-2fa-code-submit-button-loading"
										status={Status.LOADING}
									/>
								)}
							</div>
						</form>
					</>
				)}
				{otpRecoveryCodes && (
					<div className="two-factor-auth-modal-recovery-codes">
						<span className="two-factor-auth-modal-recovery-codes-text">
							<span className="two-factor-auth-modal-recovery-codes-text-success">
								You have successfully enabled 2FA!
							</span>
							<br />
							Please save the following 10 recovery codes. Any of
							them will allow you to reset your 2FA when you lose
							your device.
						</span>
						{otpRecoveryCodes.map((code: string) => (
							<div className="two-factor-auth-modal-recovery-code">
								{code}
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default TwoFactorAuthModal;
